/**
 * Dynamic sample data for demo mode.
 * Dates are generated relative to TODAY so the demo always shows a
 * realistic mix of current-month + last-month transactions.
 *
 * This means:
 *   - Current month transactions → triggers "Left This Month" view
 *   - Last month transactions → gives context & comparison data
 */

export const SAMPLE_INCOME = 55000;

// ── Date helpers ──

function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
}

function fmtDate(d) {
    return d.toISOString().split('T')[0]; // "2026-04-07"
}

function fmtDateTime(d, time) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${time}`;
}

function makeTxn({ id, daysBack, time, amount, party, type, category, subcategory, source, confidence, merchant_key, account }) {
    const d = daysAgo(daysBack);
    return {
        id,
        date: fmtDate(d),
        date_time: fmtDateTime(d, time),
        amount: `₹${amount.toLocaleString('en-IN')}`,
        party,
        type: type || 'DEBIT',
        category,
        subcategory,
        source: source || 'phonepe',
        confidence: confidence || 0.95,
        merchant_key: merchant_key || party.toLowerCase().replace(/\s+/g, ' '),
        raw_merchant: party,
        account: account || 'HDFC Bank - XX6417'
    };
}

// ── Transactions ──
// daysBack: 0 = today, 1 = yesterday, 7 = a week ago, 35 = last month, etc.

export const SAMPLE_TRANSACTIONS = [

    // ────────────────────────────────
    //  CURRENT MONTH (0-15 days ago)
    //  These trigger the "Left This Month" experience
    // ────────────────────────────────

    // Food & Delivery
    makeTxn({ id: 'sample_cur_01', daysBack: 1, time: '0845 pm', amount: 347, party: 'Swiggy', category: 'Food & Drinks', subcategory: 'Food Delivery', merchant_key: 'swiggy' }),
    makeTxn({ id: 'sample_cur_02', daysBack: 3, time: '0130 pm', amount: 520, party: 'Zomato', category: 'Food & Drinks', subcategory: 'Food Delivery', source: 'gpay', merchant_key: 'zomato', account: 'SBI - XX4291' }),
    makeTxn({ id: 'sample_cur_03', daysBack: 5, time: '0915 pm', amount: 189, party: 'Swiggy', category: 'Food & Drinks', subcategory: 'Food Delivery', merchant_key: 'swiggy' }),
    makeTxn({ id: 'sample_cur_04', daysBack: 2, time: '0120 pm', amount: 85, party: 'Tea Post', category: 'Food & Drinks', subcategory: 'Cafe', confidence: 0.8, merchant_key: 'tea post' }),

    // Transport
    makeTxn({ id: 'sample_cur_05', daysBack: 1, time: '0850 am', amount: 215, party: 'Ola', category: 'Transport', subcategory: 'Cab', merchant_key: 'ola' }),
    makeTxn({ id: 'sample_cur_06', daysBack: 4, time: '0640 pm', amount: 312, party: 'Uber', category: 'Transport', subcategory: 'Cab', source: 'gpay', merchant_key: 'uber', account: 'SBI - XX4291' }),

    // Groceries
    makeTxn({ id: 'sample_cur_07', daysBack: 2, time: '1100 am', amount: 1847, party: 'BigBasket', category: 'Groceries', subcategory: 'Online Grocery', merchant_key: 'bigbasket' }),
    makeTxn({ id: 'sample_cur_08', daysBack: 6, time: '0530 pm', amount: 680, party: 'Ratnadeep Supermarket', category: 'Groceries', subcategory: 'Supermarket', source: 'gpay', confidence: 0.85, merchant_key: 'ratnadeep', account: 'SBI - XX4291' }),
    makeTxn({ id: 'sample_cur_08b', daysBack: 1, time: '1145 pm', amount: 312, party: 'Blinkit', category: 'Groceries', subcategory: 'Blinkit', merchant_key: 'blinkit' }),
    makeTxn({ id: 'sample_cur_08c', daysBack: 3, time: '1030 pm', amount: 248, party: 'Zepto', category: 'Groceries', subcategory: 'Zepto', source: 'gpay', merchant_key: 'zepto', account: 'SBI - XX4291' }),
    makeTxn({ id: 'sample_cur_08d', daysBack: 5, time: '0910 pm', amount: 189, party: 'Blinkit', category: 'Groceries', subcategory: 'Blinkit', merchant_key: 'blinkit' }),
    makeTxn({ id: 'sample_cur_08e', daysBack: 7, time: '0045 am', amount: 156, party: 'Instamart', category: 'Groceries', subcategory: 'Instamart', merchant_key: 'instamart' }),
    makeTxn({ id: 'sample_cur_08f', daysBack: 0, time: '0840 am', amount: 40, party: 'paytm-4821@paytm', category: 'Food & Drinks', subcategory: 'Cafe', confidence: 0.5, merchant_key: 'paytm 4821' }),
    makeTxn({ id: 'sample_cur_08g', daysBack: 1, time: '0915 am', amount: 30, party: 'q918273@ybl', category: 'Food & Drinks', subcategory: 'Cafe', confidence: 0.4, merchant_key: 'q918273' }),
    makeTxn({ id: 'sample_cur_08h', daysBack: 4, time: '0810 am', amount: 25, party: 'chaiwala@oksbi', category: 'Food & Drinks', subcategory: 'Cafe', confidence: 0.5, merchant_key: 'chaiwala' }),

    // Shopping
    makeTxn({ id: 'sample_cur_09', daysBack: 3, time: '0300 pm', amount: 1299, party: 'Amazon', category: 'Shopping', subcategory: 'Online Shopping', merchant_key: 'amazon' }),

    // Subscriptions (start of month)
    makeTxn({ id: 'sample_cur_10', daysBack: 5, time: '1200 am', amount: 199, party: 'Netflix', category: 'Entertainment', subcategory: 'Streaming', merchant_key: 'netflix' }),
    makeTxn({ id: 'sample_cur_11', daysBack: 6, time: '1200 am', amount: 59, party: 'Spotify', category: 'Entertainment', subcategory: 'Streaming', merchant_key: 'spotify' }),
    makeTxn({ id: 'sample_cur_12', daysBack: 4, time: '1200 am', amount: 149, party: 'YouTube Premium', category: 'Entertainment', subcategory: 'Streaming', source: 'gpay', merchant_key: 'youtube premium', account: 'SBI - XX4291' }),

    // Personal transfer
    makeTxn({ id: 'sample_cur_13', daysBack: 2, time: '0600 pm', amount: 500, party: 'Rahul Sharma', category: 'Personal', subcategory: 'Transfer', source: 'gpay', confidence: 0.7, merchant_key: 'rahul sharma', account: 'SBI - XX4291' }),

    // Credit received this month
    makeTxn({ id: 'sample_cur_14', daysBack: 3, time: '0400 pm', amount: 1000, party: 'Vikram Reddy', type: 'CREDIT', category: 'Personal', subcategory: 'Transfer', source: 'gpay', confidence: 0.7, merchant_key: 'vikram reddy', account: 'SBI - XX4291' }),


    // ────────────────────────────────
    //  LAST MONTH (18-45 days ago)
    //  These provide comparison data & spending context
    // ────────────────────────────────

    // Food & Delivery
    makeTxn({ id: 'sample_prev_01', daysBack: 18, time: '0730 pm', amount: 280, party: 'Zomato', category: 'Food & Drinks', subcategory: 'Food Delivery', source: 'gpay', merchant_key: 'zomato', account: 'SBI - XX4291' }),
    makeTxn({ id: 'sample_prev_02', daysBack: 22, time: '0900 pm', amount: 290, party: 'Swiggy', category: 'Food & Drinks', subcategory: 'Food Delivery', merchant_key: 'swiggy' }),
    makeTxn({ id: 'sample_prev_03', daysBack: 25, time: '0100 pm', amount: 480, party: 'Zomato', category: 'Food & Drinks', subcategory: 'Food Delivery', source: 'gpay', merchant_key: 'zomato', account: 'SBI - XX4291' }),
    makeTxn({ id: 'sample_prev_04', daysBack: 30, time: '0830 pm', amount: 620, party: 'Bawarchi Restaurant', category: 'Food & Drinks', subcategory: 'Restaurant', confidence: 0.85, merchant_key: 'bawarchi' }),
    makeTxn({ id: 'sample_prev_05', daysBack: 35, time: '0700 pm', amount: 395, party: 'Swiggy', category: 'Food & Drinks', subcategory: 'Food Delivery', merchant_key: 'swiggy' }),
    makeTxn({ id: 'sample_prev_06', daysBack: 40, time: '0145 pm', amount: 120, party: 'Chai Point', category: 'Food & Drinks', subcategory: 'Cafe', source: 'gpay', confidence: 0.85, merchant_key: 'chai point', account: 'SBI - XX4291' }),

    // Transport
    makeTxn({ id: 'sample_prev_07', daysBack: 20, time: '0900 am', amount: 185, party: 'Ola', category: 'Transport', subcategory: 'Cab', merchant_key: 'ola' }),
    makeTxn({ id: 'sample_prev_08', daysBack: 28, time: '0700 pm', amount: 2200, party: 'Indian Oil Pump Gachibowli', category: 'Transport', subcategory: 'Fuel', source: 'gpay', confidence: 0.9, merchant_key: 'indian oil pump', account: 'SBI - XX4291' }),
    makeTxn({ id: 'sample_prev_09', daysBack: 34, time: '0700 am', amount: 2500, party: 'HP Petrol Pump Madhapur', category: 'Transport', subcategory: 'Fuel', confidence: 0.9, merchant_key: 'hp petrol pump' }),

    // Groceries
    makeTxn({ id: 'sample_prev_10', daysBack: 23, time: '1130 am', amount: 1650, party: 'BigBasket', category: 'Groceries', subcategory: 'Online Grocery', merchant_key: 'bigbasket' }),
    makeTxn({ id: 'sample_prev_11', daysBack: 32, time: '0500 pm', amount: 920, party: 'DMart', category: 'Groceries', subcategory: 'Supermarket', confidence: 0.9, merchant_key: 'dmart' }),
    makeTxn({ id: 'sample_prev_12', daysBack: 38, time: '0600 pm', amount: 2140, party: 'DMart', category: 'Groceries', subcategory: 'Supermarket', confidence: 0.9, merchant_key: 'dmart' }),

    // Shopping
    makeTxn({ id: 'sample_prev_13', daysBack: 26, time: '0700 pm', amount: 1999, party: 'Amazon', category: 'Shopping', subcategory: 'Online Shopping', merchant_key: 'amazon' }),
    makeTxn({ id: 'sample_prev_14', daysBack: 33, time: '0800 pm', amount: 899, party: 'Myntra', category: 'Shopping', subcategory: 'Online Shopping', source: 'gpay', merchant_key: 'myntra', account: 'SBI - XX4291' }),

    // Subscriptions
    makeTxn({ id: 'sample_prev_15', daysBack: 35, time: '1200 am', amount: 199, party: 'Netflix', category: 'Entertainment', subcategory: 'Streaming', merchant_key: 'netflix' }),
    makeTxn({ id: 'sample_prev_16', daysBack: 37, time: '1200 am', amount: 59, party: 'Spotify', category: 'Entertainment', subcategory: 'Streaming', merchant_key: 'spotify' }),
    makeTxn({ id: 'sample_prev_17', daysBack: 36, time: '1200 am', amount: 149, party: 'YouTube Premium', category: 'Entertainment', subcategory: 'Streaming', source: 'gpay', merchant_key: 'youtube premium', account: 'SBI - XX4291' }),

    // Bills
    makeTxn({ id: 'sample_prev_18', daysBack: 30, time: '1000 am', amount: 1180, party: 'TSSPDCL Electricity', category: 'Bill', subcategory: 'Electricity', confidence: 0.9, merchant_key: 'tsspdcl' }),
    makeTxn({ id: 'sample_prev_19', daysBack: 32, time: '0200 pm', amount: 599, party: 'ACT Fibernet', category: 'Bill', subcategory: 'Internet', confidence: 0.9, merchant_key: 'act fibernet' }),

    // Rent
    makeTxn({ id: 'sample_prev_20', daysBack: 37, time: '0900 am', amount: 12000, party: 'Mohammed Khaleel', category: 'Home', subcategory: 'Rent', confidence: 0.7, merchant_key: 'mohammed khaleel' }),

    // Personal transfers
    makeTxn({ id: 'sample_prev_21', daysBack: 24, time: '0430 pm', amount: 300, party: 'Ankit Verma', category: 'Personal', subcategory: 'Transfer', source: 'gpay', confidence: 0.7, merchant_key: 'ankit verma', account: 'SBI - XX4291' }),
    makeTxn({ id: 'sample_prev_22', daysBack: 29, time: '0230 pm', amount: 200, party: 'Priya K', category: 'Personal', subcategory: 'Transfer', confidence: 0.7, merchant_key: 'priya k' }),

    // Medical
    makeTxn({ id: 'sample_prev_23', daysBack: 27, time: '1100 am', amount: 450, party: 'Apollo Pharmacy', category: 'Medical', subcategory: 'Pharmacy', confidence: 0.9, merchant_key: 'apollo pharmacy' }),
];

export const SAMPLE_FIXED_EXPENSES = [
    { name: 'House Rent', amount: 12000, type: 'emi', frequency: 'monthly', lastPaidDate: fmtDate(daysAgo(7)) },
    { name: 'Netflix', amount: 199, type: 'subscription', frequency: 'monthly', lastPaidDate: fmtDate(daysAgo(5)), isAutoDetected: true },
    { name: 'Spotify', amount: 59, type: 'subscription', frequency: 'monthly', lastPaidDate: fmtDate(daysAgo(6)), isAutoDetected: true },
    { name: 'YouTube Premium', amount: 149, type: 'subscription', frequency: 'monthly', lastPaidDate: fmtDate(daysAgo(4)), isAutoDetected: true },
    { name: 'ACT Fibernet', amount: 599, type: 'bill', frequency: 'monthly', lastPaidDate: fmtDate(daysAgo(6)) },
    { name: 'Electricity', amount: 1200, type: 'bill', frequency: 'monthly', lastPaidDate: fmtDate(daysAgo(8)) },
];

export const SAMPLE_BUDGETS = [
    { category: 'leak:dining', limit: 6000, label: 'Dining & Delivery' },
    { category: 'leak:qcommerce', limit: 4000, label: 'Quick Commerce & Groceries' },
    { category: 'leak:transit', limit: 3000, label: 'Transit & Commute' },
];

export const SAMPLE_GOALS = [
    { name: 'Goa Trip', targetAmount: 15000, savedSoFar: 5000, deadline: fmtDate(new Date(new Date().getFullYear(), new Date().getMonth() + 3, 0)) },
];
